const cookieEN = {
  title: "Cookie Policy",
  effectiveDate: "May 24, 2026",
  intro: `At Agapaé, we believe in transparency about how we use technology on our Platform. This Cookie Policy explains what cookies and similar technologies we use, why we use them, and how you can manage your preferences.

The use of cookies on our Platform is governed by Greek Law 3471/2006 (as in force), the General Data Protection Regulation (EU) 2016/679 (GDPR), and Directive 2/2020 of the Hellenic Data Protection Authority (HDPA).

This policy should be read alongside our Privacy Policy, which provides further detail about how we handle your personal data.`,

  sections: [
    {
      heading: "1. What Are Cookies?",
      content: [
        `Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites function properly, improve user experience, and provide information to site operators. Depending on their use, certain cookies may constitute processing of personal data within the meaning of the GDPR.`,
        `In addition to cookies, websites may also use local storage — a similar mechanism that allows websites to store data in your browser. Unlike cookies, local storage data is not sent to the server with every request.`,
      ],
    },
    {
      heading: "2. Cookies We Use",
      content: [
        `We use a minimal number of cookies, focused solely on essential functionality. We do not use advertising or marketing cookies.`,
        {
          type: "table",
          headers: [
            "Cookie Name",
            "Provider",
            "Purpose",
            "Category",
            "Duration",
          ],
          rows: [
            [
              "agapae-consent",
              "Agapaé (1st party)",
              "Stores your cookie consent preferences so we remember your choice across visits and subdomains",
              "Essential",
              "1 year",
            ],
          ],
        },
      ],
    },
    {
      heading: "3. Local Storage We Use",
      content: [
        `For transparency, we also disclose the following browser local storage items used by our application. These are essential for the Platform to function and are not shared with third parties:`,
        {
          type: "table",
          headers: ["Key", "Purpose", "Category"],
          rows: [
            [
              "wedding_token",
              "Stores your authentication session token to keep you signed in",
              "Essential",
            ],
            [
              "wedding_refresh_token",
              "Enables session continuity so you don't have to sign in repeatedly",
              "Essential",
            ],
            [
              "session_user",
              "Stores your user session data for the current login",
              "Essential",
            ],
          ],
        },
        `The authentication items above are cleared when you log out of the Platform.`,
        `In addition, the Platform stores a small number of operational items in your browser's local storage and session storage to remember your interface preferences and to support routine flows. These include your language preference, navigation and theme settings, and short-lived flags that help the Platform recover from page reloads or remember where you were in a flow. These items are first-party only, are never shared with third parties, and can be removed at any time by clearing your browser's site data for Agapaé.`,
      ],
    },
    {
      heading: "4. Cookie Categories",
      content: [
        {
          type: "emphasis",
          text: "Essential Cookies (Always Active)",
        },
        `These cookies and storage items are strictly necessary for the Platform to function. They support core features such as user authentication and consent management. Pursuant to Article 4(5) of Greek Law 3471/2006, strictly necessary cookies do not require your consent and cannot be disabled without affecting the functionality of the Platform.`,
        {
          type: "emphasis",
          text: "Analytics Cookies",
        },
        `We do not currently use any analytics cookies or tracking scripts (such as Google Analytics, Mixpanel, or similar services). If we introduce analytics in the future, we will update this policy and request your consent before activating any analytics cookies.`,
        {
          type: "emphasis",
          text: "Marketing Cookies",
        },
        `We do not use any marketing or advertising cookies. We do not serve targeted advertisements and do not use tracking pixels from advertising platforms (such as Facebook Pixel or Google Ads). Your browsing activity on Agapaé is not tracked for marketing purposes.`,
      ],
    },
    {
      heading: "5. Third-Party Cookies and Scripts",
      content: [
        `Our Platform loads fonts from Google Fonts. When your browser requests font files, Google receives your IP address as part of the standard web request. Google Fonts does not set tracking cookies on your device.`,
        `Our payment processor, Stripe, may set cookies on their own domain when you interact with the payment interface. These cookies are governed by Stripe's own cookie policy and are necessary for secure payment processing.`,
        {
          type: "emphasis",
          text: "Sentry (Error and Performance Monitoring)",
        },
        `We use Sentry (operated by Functional Software, Inc.) to detect, diagnose, and fix bugs and performance issues in the Platform. The Sentry browser SDK runs in your browser, does not set browser cookies, and reports error and performance events to Sentry over HTTPS. These reports include your IP address, browser and device characteristics, the page where the issue occurred, and, when you are signed in, your account identifier. Sentry is used solely for operational reliability and is not used for analytics, profiling, or advertising. International transfers to Sentry are governed by EU Standard Contractual Clauses; see our Privacy Policy for further detail.`,
        {
          type: "emphasis",
          text: "Cloudflare Turnstile (Bot Protection)",
        },
        `We use Cloudflare Turnstile to protect sign-up, sign-in, RSVP, and contact forms from automated abuse. The Turnstile widget is loaded from "challenges.cloudflare.com" and may store small bot-protection signals on Cloudflare's own domain (not on agapae.gr). These signals are used only to distinguish humans from bots and are not used for advertising or cross-site tracking. International transfers are governed by EU Standard Contractual Clauses.`,
        `Beyond Google Fonts, Stripe, Sentry, and Cloudflare Turnstile described above, we do not embed social media widgets, advertising scripts, or analytics or marketing trackers on our Platform.`,
      ],
    },
    {
      heading: "6. How to Manage Your Cookie Preferences",
      content: [
        `When you first visit our Platform, you will see a cookie consent banner that allows you to:`,
        {
          type: "list",
          items: [
            `Accept all cookies;`,
            `Refuse non-essential cookies (only essential cookies will be used).`,
          ],
        },
        `Because we do not currently use any non-essential cookies, the practical outcome of both choices is the same today — your selection is recorded so that, if we ever introduce non-essential categories in the future, your existing preference is respected.`,
        `Your consent preference is stored in the "agapae-consent" cookie and is shared across all Agapaé subdomains (agapae.gr, app.agapae.gr, invite.agapae.gr) so you only need to set your preference once.`,
      ],
    },
    {
      heading: "7. How to Withdraw Consent",
      content: [
        `You can change or withdraw your cookie consent at any time through the following methods:`,
        {
          type: "list",
          items: [
            `Clear your browser cookies for agapae.gr — the consent banner will appear again on your next visit, allowing you to make a new selection;`,
            `Use your browser's built-in cookie management settings to delete the "agapae-consent" cookie specifically.`,
          ],
        },
        {
          type: "emphasis",
          text: "Browser Settings",
        },
        `Most web browsers allow you to control cookies through their settings. You can typically find these options in the "Privacy" or "Security" section of your browser preferences. Please note that blocking all cookies may affect the functionality of the Platform, particularly authentication.`,
      ],
    },
    {
      heading: "8. Changes to This Cookie Policy",
      content: [
        `We may update this Cookie Policy to reflect changes in our practices or for other operational, legal, or regulatory reasons. When we make changes, we will update the "Effective date" shown at the top of this policy.`,
        `If we introduce new cookie categories (such as analytics or marketing), we will notify you and obtain your consent before activating them.`,
      ],
    },
    {
      heading: "9. Contact Us",
      content: [
        `If you have questions about our use of cookies or this policy, please contact us:`,
        {
          type: "list",
          items: [
            `Email: privacy@agapae.gr`,
            `Company: EnQube SMPC`,
            `Registered office: Patras, Greece`,
          ],
        },
      ],
    },
  ],
};

export default cookieEN;
